import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import ReactJWPlayer from 'react-jw-player';
import { DecisionOverlay } from './Feedback/Desktop/decisionOverlay';
import { DecisionFeedback } from './Feedback/Desktop/decisionFeedback';
import DecisionPlayerProgressbar from './Feedback/DecisionPlayerProgressbar';
import StartFeedback from './Feedback/Desktop/StartFeedback';
import classes from 'classnames';
import Spinner from '../Spinner';
import { FlexDiv } from '../design-system/containers';
import StartFeedbackMobile from './Feedback/Mobile/StartFeedbackMobile';
import DecisionOverlayMobile from './Feedback/Mobile/DecisionOverlayMobile';
import { DecisionFeedbackMobile } from './Feedback/Mobile/DecisionFeedbackMobile';
import { useTranslation } from 'react-i18next';

const CONSTANTS = {
    EVENTS: {
        LOGIN: 1,
        LOGOUT: 2,
        SUBJECT_OPEN: 3,
        SUBJECT_CLOSE: 4,
        ANSWARE_CORRECT: 5,
        ANSWARE_INCORRECT: 6,
        VIDEO_START: 7,
        VIDEO_25: 8,
        VIDEO_50: 9,
        VIDEO_75: 10,
        VIDEO_95: 11,
        VIDEO_100: 12,
        CAMPAIGN_EMAIL: 13,
    },

    USERS_VIEW: {
        LISTS: 1,
        EDIT: 2,
        ADD_USERS: 3,
        LIST_USERS: 4,
        LIST_VIEW: 5,
    },
};

export const VideoPlayer = observer(
    ({
        subject,
        logEvent,
        videoPlayerUrl,
        token,
        videoTokenRequested,
        presetCaptionsLanguage,
        captionsSettings,
        onChange,
        enableAssessmentFinish,
        isAssessment,
        onLastDecision,
        resetDecisions,
        onFinishedWatchingAnswer,
        canFastForward = true,
    }) => {
        const { t } = useTranslation();

        const [videoDecisions, setVideoDecisions] = useState(null);
        const [showOverlay, setShowingOverlay] = useState(false);
        const [isAtEnd, setIsAtEnd] = useState(false);
        const [isPaused, setIsPaused] = useState(true);
        const [isReady, setIsReady] = useState(false);
        const [currentPlaylistIndex, setCurrentPlaylistIndex] = useState(0);
        const [showFeedback, setShowFeedback] = useState(false);
        const [showStartFeedback, setShowStartFeedback] = useState(false);
        const [isPlayingDecisionAnswer, setPlayingDecisionAnswer] =
            useState(false);
        const [counter, setProgress] = useState(0);
        const [maxWatchedPosition, setMaxWatchedPosition] = useState(0);

        /////////////////////////////////////////////////////////////
        /// Re-render functionality to fix JWPlayer not refreshing on subject change
        const [rerender, setRerender] = useState(true);
        useEffect(() => {
            setRerender(true);
            setMaxWatchedPosition(0);
        }, [subject]);

        useEffect(() => {
            if (rerender) {
                setRerender(false);
            }
        }, [rerender]);
        /////////////////////////////////////////////////////////////

        useEffect(() => {
            if (subject && subject.video_decisions) {
                if (!isAssessment) {
                    setShowFeedback(
                        isReady && !showStartFeedback && subject.hasBeenTried
                    );
                    setIsAtEnd(
                        !isPlayingDecisionAnswer && subject.hasBeenTried
                    );
                }
                setVideoDecisions(subject.video_decisions);
            }
        }, [subject, isReady, showStartFeedback]);

        useEffect(() => {
            const interval = setInterval(() => {
                setProgress((oldValue) => {
                    const newValue = isPaused ? oldValue : oldValue + 0.5;
                    return newValue;
                });
            }, 500);
            return () => clearInterval(interval);
        }, [isPaused]);

        const onError = (ev, data) => {
            if (ev.code === 232403) {
                videoTokenRequested(subject.video_id_jw);
            }
            if (ev.code === 224003) {
                videoTokenRequested(subject.id);
            }
        };

        const onSetupError = (ev) => {
            if (ev.code === 102403) {
                videoTokenRequested(subject.id);
            }
            if (ev.code === 224003) {
                videoTokenRequested(subject.id);
            }
        };

        const setCaptionsLanguage = () => {
            let captionsIndex = 0;
            if (presetCaptionsLanguage) {
                const captions = window.jwplayer().getCaptionsList();
                for (let i = 0; i < captions.length; i++) {
                    if (captions[i].label === presetCaptionsLanguage) {
                        captionsIndex = i;
                    }
                }
            }
            window.jwplayer().setCurrentCaptions(captionsIndex);
            hideNextUpPopupWindow();
        };

        const getPlayerCustomProps = () => {
            let props = { skin: { name: 'AwareGO' } };
            if (captionsSettings) {
                props.captions = JSON.parse(captionsSettings);
            }
            return props;
        };

        const handleNinetyFivePercent = () => {
            if (subject && subject.video_decisions) {
                window.jwplayer().pause();
                setIsPaused(true);
                if (
                    !videoDecisions[window.jwplayer().getPlaylistIndex()]
                        .decision_1 ||
                    isPlayingDecisionAnswer
                ) {
                    //If this index points to a decision question without a decision, don't pause
                    setIsAtEnd(true);
                    if (isAssessment) {
                        //If it is an assessment decision, it ends here
                        enableAssessmentFinish();
                    } else {
                        //Showing the feedback modal (With 'Try Again' and 'See answer')
                        setShowFeedback(true);
                        if (isPlayingDecisionAnswer) {
                            onFinishedWatchingAnswer();
                            onLastDecision({ previewed: true });
                        } else {
                            onLastDecision();
                        }
                    }
                } else {
                    setIsAtEnd(false);
                }
                setShowingOverlay(true);
            }
        };

        const onAnswer = (decisionQuestionId, decision) => {
            setCurrentPlaylistIndex(decision.playlistIndex);
            setShowingOverlay(false);
            if (isAssessment) {
                onChange(decisionQuestionId, decision);
            } else {
                onChange(decision, currentPlaylistIndex);
            }
            window.jwplayer().playlistItem(decision.playlistIndex);
        };

        const hideNextUpPopupWindow = () => {
            const nextUpElement = window
                .jwplayer()
                .getContainer()
                .querySelector('.jw-nextup-container');
            if (nextUpElement) {
                nextUpElement.style.display = 'none';
            }
        };

        const tryAgainDecisionSubject = () => {
            setProgress(0);
            isPlayingDecisionAnswer && setPlayingDecisionAnswer(false);
            isPlayingDecisionAnswer && videoTokenRequested(subject.id);
            setIsAtEnd(false);
            setCurrentPlaylistIndex(0);
            setShowFeedback(false);
            setShowingOverlay(false);
            setMaxWatchedPosition(0);
            window.jwplayer().playlistItem(0);
            resetDecisions();
        };

        const showAnswerVideo = () => {
            setProgress(0);
            videoTokenRequested(subject.answerSubjectId);
            setCurrentPlaylistIndex(0);
            setPlayingDecisionAnswer(true);
            setShowingOverlay(false);
            setIsAtEnd(false);
            setMaxWatchedPosition(0);
            resetDecisions();
        };

        const handleSeek = (event) => {
            if (!canFastForward) {
                const currentPosition = event.position;
                const offset = event.offset;

                // If seeking forward beyond max watched position, prevent it
                if (offset > maxWatchedPosition) {
                    window.jwplayer().seek(maxWatchedPosition);
                }
            }
        };

        const handleTime = (event) => {
            if (!canFastForward) {
                const currentPosition = event.position;
                if (currentPosition > maxWatchedPosition) {
                    setMaxWatchedPosition(currentPosition);
                }
            }
        };

        const shouldShowQuestionOverlay = () => {
            return (
                videoDecisions &&
                showOverlay &&
                !showFeedback &&
                videoDecisions[currentPlaylistIndex] &&
                (videoDecisions[currentPlaylistIndex].questionText || isAtEnd)
            );
        };

        const startVideo = () => {
            window.jwplayer().play();
            setShowStartFeedback(false);
        };

        if (!token) {
            return (
                <div className="video">
                    <div
                        className="spinner-table small"
                        style={{ textAlign: 'center' }}
                    >
                        <Spinner />
                    </div>
                </div>
            );
        } else if (!token.videoId) {
            return (
                <div className="video">
                    {subject.title} video not available.
                </div>
            );
        } else {
            const playlist =
                token &&
                `https://content.jwplatform.com/feeds/${token.videoId}.json${token.sig_feed}`;

            const customProps = getPlayerCustomProps();

            return Modernizr.video ? (
                videoPlayerUrl ? (
                    <>
                        <FlexDiv column positionRelative>
                            {shouldShowQuestionOverlay() && (
                                <DecisionOverlay
                                    decisionQuestion={
                                        videoDecisions[currentPlaylistIndex]
                                    }
                                    onAnswer={onAnswer}
                                    isAtEnd={isAtEnd}
                                />
                            )}
                            {showFeedback && !isPlayingDecisionAnswer && (
                                <DecisionFeedback
                                    subject={subject}
                                    showAnswerVideo={showAnswerVideo}
                                    tryAgain={tryAgainDecisionSubject}
                                />
                            )}
                            {showStartFeedback && (
                                <StartFeedback
                                    startDecisionsSubjectVideo={startVideo}
                                />
                            )}

                            {!rerender && (
                                <ReactJWPlayer
                                    className={classes('video-item', {
                                        'decision-video-item':
                                            subject.video_decisions,
                                    })}
                                    image={subject.thumb}
                                    playerId={subject.slug || 'video-player'}
                                    playerScript={videoPlayerUrl}
                                    licenseKey="tbMPLx6pAOjLHsSbrqGJdPVygT90S8O71WG28+3dDJA="
                                    playlist={playlist}
                                    width="100%"
                                    aspectratio="16:9"
                                    responsive={true}
                                    isAutoPlay={false}
                                    onSetupError={() => onSetupError()}
                                    onError={() => onError()}
                                    allowFullscreen={false}
                                    onTwentyFivePercent={() =>
                                        logEvent(
                                            subject,
                                            CONSTANTS.EVENTS.VIDEO_25
                                        )
                                    }
                                    onFiftyPercent={() =>
                                        logEvent(
                                            subject,
                                            CONSTANTS.EVENTS.VIDEO_50
                                        )
                                    }
                                    onSeventyFivePercent={() =>
                                        logEvent(
                                            subject,
                                            CONSTANTS.EVENTS.VIDEO_75
                                        )
                                    }
                                    onNinetyFivePercent={() => {
                                        handleNinetyFivePercent();
                                        logEvent(
                                            subject,
                                            CONSTANTS.EVENTS.VIDEO_95
                                        );
                                    }}
                                    onOneHundredPercent={() =>
                                        logEvent(
                                            subject,
                                            CONSTANTS.EVENTS.VIDEO_100
                                        )
                                    }
                                    onPlay={() => {
                                        logEvent(
                                            subject,
                                            CONSTANTS.EVENTS.VIDEO_START,
                                            token
                                        );
                                        setIsPaused(false);
                                        //subject.isStarted();
                                    }}
                                    onResume={() => {
                                        setIsPaused(false);
                                    }}
                                    onPause={() => {
                                        setIsPaused(true);
                                    }}
                                    onReady={() => {
                                        setCaptionsLanguage();
                                        setIsReady(true);
                                        setShowStartFeedback(
                                            subject.type === 3 &&
                                                !isAtEnd &&
                                                !showFeedback &&
                                                !isPlayingDecisionAnswer &&
                                                !subject.hasBeenTried
                                        );
                                        if (isPlayingDecisionAnswer) {
                                            window.jwplayer().play();
                                        }
                                    }}
                                    onSeek={handleSeek}
                                    onTime={handleTime}
                                    customProps={customProps}
                                />
                            )}
                        </FlexDiv>
                        {subject.type === 3 && isReady && (
                            <DecisionPlayerProgressbar
                                counter={counter}
                                totalLength={
                                    isPlayingDecisionAnswer
                                        ? subject.answerSubjectLength
                                        : videoDecisions &&
                                          videoDecisions[
                                              currentPlaylistIndex
                                          ] &&
                                          videoDecisions[currentPlaylistIndex]
                                              .longestDecisionTree
                                }
                            />
                        )}
                        {shouldShowQuestionOverlay() && (
                            <DecisionOverlayMobile
                                decisionQuestion={
                                    videoDecisions[currentPlaylistIndex]
                                }
                                onAnswer={onAnswer}
                                isAtEnd={isAtEnd}
                            />
                        )}
                        {showStartFeedback && (
                            <StartFeedbackMobile
                                startDecisionsSubjectVideo={startVideo}
                            />
                        )}
                        {showFeedback && (
                            <DecisionFeedbackMobile
                                subject={subject}
                                showAnswerVideo={showAnswerVideo}
                                tryAgain={tryAgainDecisionSubject}
                            />
                        )}
                    </>
                ) : null
            ) : (
                <div className="alert alert-danger">
                    {t(
                        'Your browser does not currently recognize any of the video formats available'
                    )}
                </div>
            );
        }
    }
);
